import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import {
  heroContent,
  values,
  productHighlights,
  seoByRoute,
  mediaAssets,
} from '../data/siteContent';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.12 * i, ease: 'easeOut' },
  }),
};

export default function Home() {
  const seo = seoByRoute['/'];

  return (
    <>
      <SEO {...seo} />
      <section className="relative flex min-h-screen items-center overflow-hidden text-white">
        <iframe
          src="https://www.youtube.com/embed/2PKiM5XVjdo?autoplay=1&mute=1&loop=1&playlist=2PKiM5XVjdo&controls=0&modestbranding=1"
          title="Duoley Processing Line"
          className="absolute inset-0 h-full w-full object-cover border-0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-black/45" />
        <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-14 px-6 pb-24 pt-32 md:pt-40 lg:flex-row lg:items-center">
          <motion.div
            className="max-w-2xl"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-secondary">
              {heroContent.eyebrow}
            </p>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
              {heroContent.heading}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">
              {heroContent.description}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={heroContent.primaryCta.to}
                className="btn-primary shadow-lg shadow-primary/40"
              >
                {heroContent.primaryCta.label}
              </Link>
              <Link
                to={heroContent.secondaryCta.to}
                className="btn-on-dark"
              >
                {heroContent.secondaryCta.label}
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
          >
            {heroContent.stats.map(({ value, label }, index) => (
              <motion.div
                key={label}
                className="rounded-3xl border border-white/15 bg-white/10 px-4 py-8 text-center backdrop-blur-md"
                variants={fadeUp}
                custom={index}
              >
                <p className="font-display text-3xl font-semibold text-secondary">{value}</p>
                <p className="mt-2 text-sm text-white/80">{label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-6xl px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr]">
          <motion.div
            className="space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <h2 className="font-display text-3xl font-semibold text-text sm:text-4xl">
              Nourishing families with integrity
            </h2>
            <p className="text-lg text-slate-600">
              Duoley unites Sidama’s smallholder farmers and modern processing to bring nutrient-rich dairy and fruit
              juice to households across Ethiopia. Our cold-chain logistics and circular packaging ensure every bottle
              stays as fresh as the day it was crafted.
            </p>
            <div className="grid gap-6 md:grid-cols-3">
              {values.map((value, idx) => (
                <motion.div
                  key={value.title}
                  className="rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-6 shadow-lg shadow-slate-100 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 group"
                  variants={fadeUp}
                  custom={idx}
                  whileHover={{ y: -4 }}
                >
                  <p className="font-display text-lg text-text text-center group-hover:text-primary transition-colors">{value.title}</p>
                  <p className="mt-3 text-sm text-slate-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div
            className="relative overflow-hidden rounded-[32px] shadow-2xl group"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <img src={heroContent.image} alt="Duoley farmland" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 via-transparent to-secondary/40" />
          </motion.div>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-6xl px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <div className="flex flex-col items-center text-center">
            <p className="text-sm uppercase tracking-[0.3em] text-primary">Product Spotlight</p>
            <h2 className="mt-4 font-display text-3xl text-text sm:text-4xl">Crafted for flavor and wellness</h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Every product is thoughtfully processed with nutrient preservation and minimal waste. Explore the highlights
              that keep our community coming back to Duoley.
            </p>
          </div>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {productHighlights.map((product, index) => (
              <motion.article
                key={product.title}
                className="flex h-full flex-col rounded-3xl bg-white p-6 shadow-xl shadow-slate-200/60 hover:shadow-2xl hover:shadow-slate-300/50 transition-all duration-300 group border border-slate-100"
                variants={fadeUp}
                custom={index}
                whileHover={{ y: -8 }}
              >
                <div className="overflow-hidden rounded-2xl relative">
                  <img src={product.image} alt={product.title} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="mt-6 font-display text-xl text-text group-hover:text-primary transition-colors">{product.title}</h3>
                <p className="mt-3 flex-1 text-sm text-slate-600">{product.description}</p>
                <ul className="mt-4 space-y-2 text-xs uppercase tracking-wide text-slate-500">
                  {product.details.map((detail) => (
                    <li key={detail} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.article>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/products" className="btn-primary">
              View all products
            </Link>
          </div>
        </motion.div>
      </section>

      <section className="relative mt-24 bg-surface">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="glass-panel relative grid gap-10 overflow-hidden rounded-[32px] px-10 py-12 shadow-xl md:grid-cols-[1.1fr_0.9fr]">
            <div>
              <h2 className="font-display text-3xl text-text sm:text-4xl">Rooted in Sidama, reaching far beyond</h2>
              <p className="mt-4 text-base text-slate-600">
                We actively collaborate with local schools, nutritionists, and hospitality partners to deliver products
                that cater to evolving tastes while honoring tradition. Discover how you can partner with us to grow your
                menu or retail shelves.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary">
                  Become a distributor
                </Link>
                <Link to="/about" className="btn-secondary">
                  Learn our story
                </Link>
              </div>
            </div>
            <motion.div
              className="grid gap-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              {[1, 2, 3].map((index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  className="rounded-3xl border border-white/40 bg-white/70 backdrop-blur-sm px-6 py-5 text-sm text-slate-600 shadow-lg hover:bg-white/80 transition-all"
                  whileHover={{ x: 8 }}
                >
                  {index === 1 && '✓ Certified cold-chain distribution across southern Ethiopia.'}
                  {index === 2 && '✓ Community-led sourcing with transparent pricing to uplift farmers.'}
                  {index === 3 && '✓ Immersive tasting experiences and agritourism on our flagship farm.'}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-6xl px-6">
        <div className="grid gap-10 rounded-[32px] bg-white px-8 py-12 shadow-xl shadow-slate-200/70 md:grid-cols-[0.95fr_1.05fr]">
          <motion.div
            className="overflow-hidden rounded-[28px] group"
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div className="relative overflow-hidden aspect-video bg-slate-900 rounded-[28px]">
              <iframe
                src={mediaAssets.farmTourYoutube}
                title="Duoley Farm Tour"
                className="h-full w-full border-0 group-hover:scale-105 transition-transform duration-500"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          </motion.div>
          <div className="space-y-6 text-slate-600">
            <p className="text-xs uppercase tracking-[0.35em] text-primary">Farm immersion</p>
            <h2 className="font-display text-3xl text-text sm:text-4xl">Walk the fields with our producer network</h2>
            <p>
              Experience the regenerative practices, animal welfare standards, and harvesting rituals that make every
              Duoley product distinctive. Our guided tours spotlight the families and co-ops powering the Sidama food
              ecosystem.
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-primary" />
                Seasonal sensory tastings across dairy and juice collections.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-primary" />
                Hands-on workshops in fermentation, cold-pressing, and responsible packaging.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-2 w-2 rounded-full bg-primary" />
                Community meals featuring endemic Sidama ingredients and storytelling.
              </li>
            </ul>
            <Link to="/contact" className="btn-primary inline-flex w-fit">
              Plan a visit
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
